import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CalendarClock, Check, Loader2, ShieldAlert, X } from "lucide-react";
import { fmtDate } from "../proposal/format";

const API = process.env.REACT_APP_BACKEND_URL;

// Medicys only panel on the staff view of a proposal: approve the
// deadline the client asked about, or refuse it silently and record
// the workable date offered by email. Hidden until a check exists.
// What the BD has to do, and what the app does, once a decision is recorded.
function DeadlineNextSteps({ check, clientName }) {
  const approved = check.decision === "approved";
  const who = clientName || check.requested_by;
  const steps = approved
    ? [
        <><span className="font-semibold text-stone-100">Email {who}</span> ({check.requested_by}) to tell them the deadline of {fmtDate(check.deadline)} is confirmed and that they can now resume commissioning from their proposal link. The app does not send this email.</>,
        <>Their form has already unlocked: the deadline warning in their viewer is green and the remaining sections and the signing step are available again.</>,
        <>Nothing else to do here. When they sign you receive the countersignature email.</>,
      ]
    : [
        <><span className="font-semibold text-stone-100">Email {who}</span> ({check.requested_by}) to tell them the deadline of {fmtDate(check.deadline)} is not workable{check.workable_from ? <> and that Medicys can deliver by <span className="font-semibold text-stone-100">{fmtDate(check.workable_from)}</span></> : ""}. The app does not send this email; their form stays blocked at the deadline field until they act.</>,
        <>Once they enter the workable date, or any later date that allows the quoted working days, the rest of the form reappears and they resume commissioning without any further action from you.</>,
        <>If they insist on {fmtDate(check.deadline)}, you can still approve it here.</>,
      ];
  return (
    <div className={`rounded-lg border p-4 flex flex-col gap-3 text-sm ${approved ? "border-emerald-400/30 bg-emerald-500/10" : "border-rose-400/30 bg-rose-500/10"}`} data-testid="staff-deadline-next" data-decision={check.decision}>
      <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-stone-300">What happens next</p>
      <ol className="list-decimal pl-5 flex flex-col gap-1.5 text-stone-200 leading-relaxed">
        {steps.map((s, i) => <li key={i}>{s}</li>)}
      </ol>
    </div>
  );
}

export function StaffDeadlinePanel({ token, initial = null, onDecide = null }) {
  const [data, setData] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [refusing, setRefusing] = useState(false);
  const [workable, setWorkable] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState(null);
  const url = `${API}/api/public/proposals/${encodeURIComponent(token)}/commission/deadline-review`;

  const load = useCallback(async () => {
    if (initial) return;
    try {
      const { data: d } = await axios.get(url, { withCredentials: true });
      setData(d);
    } catch (_e) {
      setData(null);
    }
  }, [url, initial]);

  useEffect(() => { load(); }, [load]);

  const decide = async (decision) => {
    setBusy(true);
    setError(null);
    try {
      const body = { decision, workable_from: decision === "refused" ? workable || null : null, note: note || null };
      const d = onDecide ? await onDecide(body) : (await axios.post(url, body, { withCredentials: true })).data;
      setData(d);
      setRefusing(false);
    } catch (e) {
      setError(e?.response?.data?.detail?.missing?.join(", ") || e?.response?.data?.detail || "The decision could not be saved.");
    } finally {
      setBusy(false);
    }
  };

  const check = data?.check;
  if (!check) return null;
  const cur = data.current || {};
  const decided = !!check.decision;
  const stillShort = cur.short && !cur.confirmed;
  return (
    <section className="no-print mx-4 sm:mx-6 lg:mx-10 mt-4 rounded-xl border border-[#1B4F8A]/30 bg-[#0B1220] text-stone-100 p-5 sm:p-6 flex flex-col gap-4" data-testid="staff-deadline-panel" data-decision={check.decision || "pending"}>
      <div className="flex items-start gap-3">
        <span className="w-9 h-9 rounded-full bg-[#3EA8DC]/15 text-[#9FD8F2] flex items-center justify-center shrink-0"><ShieldAlert className="w-4 h-4" /></span>
        <div className="flex flex-col gap-1.5 text-[15px] leading-relaxed">
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#9FD8F2]">Medicys only · Deadline check</p>
          <p>
            <span className="font-semibold">{data.client_name || check.requested_by}</span> ({check.requested_by}) asked on {fmtDate(check.requested_at)} whether <span className="font-semibold">{fmtDate(check.deadline)}</span> is workable:
            {" "}<span className="font-semibold">{check.available_working_days} working days</span>{check.counted_from ? <> from their planned start of {fmtDate(check.counted_from)}</> : null} against <span className="font-semibold">{check.quoted_working_days} quoted</span>.
          </p>
          {decided && (
            <p className="text-stone-300" data-testid="staff-deadline-status">
              {check.decision === "approved"
                ? `Approved by ${check.decided_by} on ${fmtDate(check.decided_at)}.`
                : `Refused by ${check.decided_by} on ${fmtDate(check.decided_at)}${check.workable_from ? `; workable from ${fmtDate(check.workable_from)}` : ""}.`}
              {check.note ? ` Note: ${check.note}` : ""}
            </p>
          )}
          {cur.deadline && cur.deadline !== check.deadline && (
            <p className="text-stone-300" data-testid="staff-deadline-current">
              The client has since entered {fmtDate(cur.deadline)} ({cur.available_working_days} working days){cur.confirmed ? ", which counts as agreed." : stillShort ? ", still shorter than quoted and not agreed." : ", which allows the quoted working days."}
            </p>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-rose-300" data-testid="staff-deadline-error">{String(error)}</p>}

      {decided && <DeadlineNextSteps check={check} clientName={data.client_name} />}

      {refusing ? (
        <div className="flex flex-col sm:flex-row sm:items-end gap-3 rounded-lg bg-white/5 p-4" data-testid="staff-deadline-refuse-form">
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-stone-300">Workable deadline you will offer by email (optional)</span>
            <input type="date" value={workable} onChange={(e) => setWorkable(e.target.value)} className="h-10 rounded-md bg-white text-stone-900 px-3 text-sm" data-testid="staff-deadline-workable" />
          </label>
          <label className="flex flex-col gap-1.5 text-sm flex-1">
            <span className="text-stone-300">Internal note (optional)</span>
            <input value={note} onChange={(e) => setNote(e.target.value)} className="h-10 rounded-md bg-white text-stone-900 px-3 text-sm" data-testid="staff-deadline-note" />
          </label>
          <div className="flex gap-2">
            <button type="button" onClick={() => decide("refused")} disabled={busy} data-testid="staff-deadline-refuse-confirm" className="inline-flex items-center gap-2 rounded-md bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white font-semibold px-4 h-10 text-sm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />} Record refusal
            </button>
            <button type="button" onClick={() => setRefusing(false)} disabled={busy} className="rounded-md border border-white/20 px-4 h-10 text-sm text-stone-200 hover:bg-white/10" data-testid="staff-deadline-refuse-cancel">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={() => decide("approved")} disabled={busy || check.decision === "approved"} data-testid="staff-deadline-approve" className="inline-flex items-center gap-2 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold px-4 h-10 text-sm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Approve this deadline
          </button>
          <button type="button" onClick={() => { setWorkable(check.workable_from || ""); setNote(check.note || ""); setRefusing(true); }} disabled={busy} data-testid="staff-deadline-refuse" className="inline-flex items-center gap-2 rounded-md border border-rose-400/60 text-rose-200 hover:bg-rose-500/10 disabled:opacity-50 font-semibold px-4 h-10 text-sm">
            <CalendarClock className="w-4 h-4" /> {check.decision === "refused" ? "Change the workable deadline" : "Refuse (silently)"}
          </button>
          <p className="text-xs text-stone-400 leading-relaxed basis-full sm:basis-auto">Approving unblocks the client in the viewer; you still confirm it to them by email. Refusing tells the client nothing; you email them the workable deadline, and if they enter it here they can carry on.</p>
        </div>
      )}
    </section>
  );
}
